import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Logo from "@/components/Logo";
import { getLangFromWindow, updateCanonical, useHreflang } from "@/utils/seoLang";

const COPY = {
  privacy: {
    ru: {
      title: "Политика конфиденциальности — YUCCA Flowers",
      h1: "Политика конфиденциальности",
      updated: "Обновлено: февраль 2026",
      sections: [
        { h: "Какие данные мы собираем", p: "При оформлении заказа или отправке заявки на свадьбу мы собираем: имя, контактные данные (телефон, WhatsApp, email), адрес доставки, информацию о заказе. При посещении сайта — анонимные данные о страницах (IP-адрес, тип устройства, реферер) для аналитики." },
        { h: "Как мы используем данные", p: "Данные используются только для выполнения заказа, доставки, связи с вами по поводу свадьбы или мероприятия, а также для улучшения сайта. Мы не передаём ваши данные третьим лицам, кроме случаев, необходимых для доставки (курьерская служба) или предусмотренных законом." },
        { h: "Cookies и аналитика", p: "Мы используем cookies для авторизации и аналитики (Google Analytics, локальная аналитика). Вы можете отключить cookies в настройках браузера." },
        { h: "Ваши права", p: "Вы можете в любое время запросить удаление ваших данных, обновление информации или экспорт. Свяжитесь с нами через WhatsApp или email." },
        { h: "Безопасность", p: "Все данные передаются по HTTPS. Платёжные операции обрабатываются через сертифицированные платёжные системы (Stripe, PayPal, банковские эквайеры) — мы не храним данные карт." },
        { h: "Контакты", p: "По всем вопросам конфиденциальности: WhatsApp +90 531 622 43 78" },
      ],
    },
    en: {
      title: "Privacy Policy — YUCCA Flowers",
      h1: "Privacy Policy",
      updated: "Updated: February 2026",
      sections: [
        { h: "What data we collect", p: "When placing an order or sending a wedding enquiry we collect: name, contact details (phone, WhatsApp, email), delivery address, order information. When visiting the site — anonymous page data (IP, device type, referrer) for analytics." },
        { h: "How we use the data", p: "Data is used solely for fulfilling your order, delivery, communicating with you about your wedding or event, and improving the site. We do not share your data with third parties except where required for delivery (courier service) or by law." },
        { h: "Cookies and analytics", p: "We use cookies for authentication and analytics (Google Analytics, in-house analytics). You can disable cookies in your browser settings." },
        { h: "Your rights", p: "You can request deletion of your data, updating information or export at any time. Contact us via WhatsApp or email." },
        { h: "Security", p: "All data is transmitted over HTTPS. Payment operations are processed via certified payment systems (Stripe, PayPal, banking acquirers) — we do not store card data." },
        { h: "Contacts", p: "For any privacy questions: WhatsApp +90 531 622 43 78" },
      ],
    },
  },
  terms: {
    ru: {
      title: "Условия использования — YUCCA Flowers",
      h1: "Условия использования",
      updated: "Обновлено: февраль 2026",
      sections: [
        { h: "Общие положения", p: "Настоящие условия регулируют использование сайта yuccaflower.com и заказ услуг доставки цветов и свадебного оформления. Размещая заказ или отправляя заявку, вы соглашаетесь с этими условиями." },
        { h: "Заказы и оплата", p: "Заказы оплачиваются полностью при оформлении или по предоплате (свадьбы — 30%). Стоимость указана в EUR, USD или TRY. Возврат возможен в случае невозможности доставки по нашей вине." },
        { h: "Доставка", p: "Доставка осуществляется по заявленным городам. Время доставки указывается при заказе и может меняться в зависимости от загруженности и погодных условий. Для свадеб установка осуществляется накануне." },
        { h: "Свадебные услуги", p: "Бронь даты свадьбы фиксируется внесением депозита в размере 30%. В случае отмены за 60+ дней до свадьбы — депозит возвращается за вычетом 10% (логистика). За 30 дней — депозит не возвращается, но переносится на новую дату." },
        { h: "Интеллектуальная собственность", p: "Все материалы сайта (фото, тексты, дизайн) защищены авторским правом. Использование без разрешения запрещено." },
        { h: "Ответственность", p: "YUCCA Flowers не несёт ответственности за форс-мажорные обстоятельства (стихийные бедствия, отмена авиарейсов и т.п.). В таких случаях стороны согласовывают перенос или возврат." },
        { h: "Контакты", p: "По всем вопросам: WhatsApp +90 531 622 43 78" },
      ],
    },
    en: {
      title: "Terms of Service — YUCCA Flowers",
      h1: "Terms of Service",
      updated: "Updated: February 2026",
      sections: [
        { h: "General", p: "These terms govern your use of yuccaflower.com and ordering of flower delivery and wedding floral design services. By placing an order or sending an enquiry you agree to these terms." },
        { h: "Orders and payment", p: "Orders are paid in full at checkout or by deposit (weddings — 30%). Prices are in EUR, USD or TRY. Refunds are possible only in the case of non-delivery on our side." },
        { h: "Delivery", p: "Delivery is performed in the listed cities. Delivery time is shown at checkout and can vary depending on workload and weather. For weddings setup is performed the day before." },
        { h: "Wedding services", p: "Wedding date is reserved with a 30% deposit. Cancellation 60+ days before — deposit refunded minus 10% (logistics). Within 30 days — deposit is non-refundable but transferable to a new date." },
        { h: "Intellectual property", p: "All site content (photos, text, design) is protected by copyright. Use without permission is prohibited." },
        { h: "Liability", p: "YUCCA Flowers is not liable for force majeure (natural disasters, flight cancellations etc). In such cases parties agree on rescheduling or refund." },
        { h: "Contacts", p: "For any questions: WhatsApp +90 531 622 43 78" },
      ],
    },
  },
};

export default function LegalPage() {
  const { type } = useParams(); // "privacy" or "terms"
  const lang = getLangFromWindow(["ru", "en"]);
  const data = COPY[type]?.[lang] || COPY.privacy.ru;
  useEffect(() => { document.title = data.title; }, [data.title]);
  // Self-referential canonical (otherwise the page keeps the static homepage
  // canonical from index.html → GSC "Alternate page with proper canonical tag").
  useHreflang(`/legal/${type}`, ["ru", "en"]);
  useEffect(() => { updateCanonical(`/legal/${type}`); }, [type]);

  return (
    <div className="bg-[#FAF5EE] min-h-screen" data-testid={`legal-${type}`}>
      <header className="bg-[#0A0807] text-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-5 sm:py-6 flex items-center justify-between">
          <Link to={`/${lang !== "ru" ? `?lang=${lang}` : ""}`}><Logo variant="gold" /></Link>
          <Link to={`/${lang !== "ru" ? `?lang=${lang}` : ""}`} className="text-[11px] tracking-[0.2em] text-white/70 hover:text-white">
            {lang === "ru" ? "НАЗАД" : lang === "tr" ? "GERİ" : "BACK"}
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-12 sm:py-20">
        <p className="text-[11px] tracking-[0.25em] text-[#B8966F] font-light mb-3">{data.updated}</p>
        <h1 className="text-3xl sm:text-5xl text-[#1F1815] mb-10" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}>
          {data.h1}
        </h1>
        <div className="space-y-8">
          {data.sections.map((s, i) => (
            <section key={i}>
              <h2 className="text-lg sm:text-xl font-medium text-[#1F1815] mb-2.5" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>{s.h}</h2>
              <p className="text-sm sm:text-base text-[#3D2F22] font-light leading-[1.8]">{s.p}</p>
            </section>
          ))}
        </div>
      </main>
    </div>
  );
}
